/**
 * ABM Localidades — Fase 8
 *
 * Filtro por provincia (selector arriba). El dialog de alta/edición permite
 * elegir provincia + nombre. Hard delete con restricción por FK desde
 * afiliados/empresas (Postgres bloquea automáticamente).
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';

import {
  useProvincias,
  useLocalidades,
  useCreateLocalidad,
  useUpdateLocalidad,
  useDeleteLocalidad,
  type Localidad,
  type Provincia,
} from '../../../hooks/useCatalogos';

export default function LocalidadesList() {
  // Filtro por provincia (afecta la query)
  const [filtroProvinciaId, setFiltroProvinciaId] = useState<string | null>(null);

  const { data: provincias = [] } = useProvincias();
  const {
    data: localidades = [],
    isLoading,
    isError,
    error,
  } = useLocalidades(filtroProvinciaId ?? undefined);

  const create = useCreateLocalidad();
  const update = useUpdateLocalidad();
  const del = useDeleteLocalidad();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Localidad | null>(null);
  const [nombre, setNombre] = useState('');
  const [provinciaId, setProvinciaId] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setNombre('');
    setProvinciaId(filtroProvinciaId ?? '');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (l: Localidad) => {
    setEditing(l);
    setNombre(l.nombre);
    setProvinciaId(l.provincia_id);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleDelete = async (l: Localidad) => {
    if (!confirm(`¿Eliminar la localidad "${l.nombre}"? Esto puede fallar si hay registros asociados.`)) return;
    try {
      await del.mutateAsync(l.id);
    } catch (e) {
      alert(`No se pudo eliminar: ${(e as Error).message}`);
    }
  };

  const handleSubmit = async () => {
    const n = nombre.trim();
    if (n.length < 2 || n.length > 100) {
      setFormError('El nombre debe tener entre 2 y 100 caracteres');
      return;
    }
    if (!provinciaId) {
      setFormError('Seleccioná la provincia');
      return;
    }
    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          payload: { nombre: n, provincia_id: provinciaId },
        });
      } else {
        await create.mutateAsync({ nombre: n, provincia_id: provinciaId });
      }
      setDialogOpen(false);
    } catch {
      // se muestra abajo
    }
  };

  const apiError = (editing ? update.error : create.error) as Error | null;
  const submitting = (editing ? update.isPending : create.isPending) || del.isPending;

  const provinciaSeleccionada =
    provincias.find((p) => p.id === provinciaId) ?? null;

  const columns = useMemo<GridColDef<Localidad>[]>(
    () => [
      { field: 'nombre', headerName: 'Localidad', flex: 1, minWidth: 200 },
      {
        field: 'provincia',
        headerName: 'Provincia',
        flex: 1,
        minWidth: 180,
        renderCell: (p: GridRenderCellParams<Localidad>) =>
          p.row.provincia?.nombre ?? '—',
      },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<Localidad>) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => openEdit(p.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton size="small" color="error" onClick={() => handleDelete(p.row)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Localidades
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Catálogo geográfico filtrable por provincia.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Nueva localidad
        </Button>
      </Stack>

      {/* Filtro por provincia */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Autocomplete
          sx={{ maxWidth: 360 }}
          size="small"
          options={provincias}
          getOptionLabel={(p) => p.nombre}
          value={provincias.find((p) => p.id === filtroProvinciaId) ?? null}
          onChange={(_, val) => setFiltroProvinciaId(val?.id ?? null)}
          renderInput={(params) => (
            <TextField {...params} label="Filtrar por provincia (todas si vacío)" />
          )}
        />
      </Card>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={localidades}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
          localeText={{ noRowsLabel: 'No hay localidades para mostrar' }}
        />
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar localidad' : 'Nueva localidad'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {apiError && <Alert severity="error">{apiError.message}</Alert>}
            {formError && <Alert severity="warning">{formError}</Alert>}
            <Autocomplete
              options={provincias}
              getOptionLabel={(p: Provincia) => p.nombre}
              value={provinciaSeleccionada}
              onChange={(_, val) => setProvinciaId(val?.id ?? '')}
              renderInput={(params) => (
                <TextField {...params} required label="Provincia" />
              )}
            />
            <TextField
              required
              fullWidth
              label="Nombre de la localidad"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {editing ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
